import { createStore } from 'zustand/vanilla';
import type { StoreApi } from 'zustand/vanilla';
import {
  applyEdgeChanges,
  applyNodeChanges,
  ConnectionMode,
  DEFAULT_CONNECTION_RADIUS,
  DEFAULT_DELETE_KEY_CODE,
  DEFAULT_MAX_ZOOM,
  DEFAULT_MIN_ZOOM,
  DEFAULT_VIEWPORT,
  getElementsToDelete,
} from '../core';
import type { DefaultEdgeOptions } from '../core';
import {
  getEffectiveDimensionChanges,
  getUnreportedSizeChanges,
  withEdges,
  withHandleBounds,
  withMeasuredSizes,
  withNodes,
  withPaneSize,
  withViewport,
  withZoomLimits,
} from './actions';
import type { MindmapState, MindmapStore, MindmapStoreInit } from './types';

export type MindmapStoreApi = StoreApi<MindmapStore>;

/** Shared empty defaults, so stores without `defaultEdgeOptions` keep a stable value. */
const NO_EDGE_DEFAULTS: DefaultEdgeOptions = {};

const initialState = ({ nodes = [], edges = [], viewport = DEFAULT_VIEWPORT, ...limits }: MindmapStoreInit): MindmapState => ({
  ...withNodes(new Map(), nodes),
  ...withEdges(edges),
  defaultEdgeOptions: NO_EDGE_DEFAULTS,
  ...withViewport(viewport),
  ...withZoomLimits(limits.minZoom ?? DEFAULT_MIN_ZOOM, limits.maxZoom ?? DEFAULT_MAX_ZOOM),
  ...withPaneSize({ width: 0, height: 0 }),
  nodeTypes: {},
  connectionMode: ConnectionMode.Strict,
  nodesDraggable: true,
  elementsSelectable: true,
  dragHandlers: {},
  connection: null,
  clickConnectStart: null,
  connectOptions: { nodesConnectable: true, connectOnClick: true, connectionRadius: DEFAULT_CONNECTION_RADIUS },
  connectHandlers: {},
  deleteHandlers: {},
  elementHandlers: {},
  deleteKeyCode: DEFAULT_DELETE_KEY_CODE,
  selectionRect: null,
  domNode: null,
});

/** One store per canvas (D-006), created by the provider that owns it (D-018). */
export const createMindmapStore = (init: MindmapStoreInit = {}): MindmapStoreApi =>
  createStore<MindmapStore>()((set, get) => ({
    ...initialState(init),
    setNodes: (nodes) => {
      set((state) => withNodes(state.nodeLookup, nodes));
      get().triggerNodeChanges(getUnreportedSizeChanges(get().nodeLookup));
    },
    setEdges: (edges) => set(withEdges(edges)),
    setDefaultEdgeOptions: (defaultEdgeOptions) => set({ defaultEdgeOptions }),
    setViewport: (viewport) => set(withViewport(viewport)),
    setZoomLimits: (minZoom, maxZoom) => set(withZoomLimits(minZoom, maxZoom)),
    setPaneSize: (paneSize) => set(withPaneSize(paneSize)),
    setNodeTypes: (nodeTypes) => set({ nodeTypes }),
    setConnectionMode: (connectionMode) => set({ connectionMode }),
    setHandleBounds: (updates) => {
      if (updates.length > 0) set((state) => withHandleBounds(state.nodeLookup, updates));
    },
    setOnNodesChange: (onNodesChange) => set({ onNodesChange }),
    updateNodeDimensions: (changes) => {
      const { nodeLookup } = get();
      const effective = getEffectiveDimensionChanges(nodeLookup, changes);
      if (effective.length === 0) return;
      set(withMeasuredSizes(nodeLookup, effective));
      get().triggerNodeChanges(effective);
    },
    setNodesDraggable: (nodesDraggable) => set({ nodesDraggable }),
    setInteractive: (isInteractive) =>
      set((state) => ({
        nodesDraggable: isInteractive,
        elementsSelectable: isInteractive,
        connectOptions: { ...state.connectOptions, nodesConnectable: isInteractive },
      })),
    setDragHandlers: (dragHandlers) => set({ dragHandlers }),
    triggerNodeChanges: (changes) => {
      if (changes.length === 0) return;
      const { onNodesChange } = get();
      if (onNodesChange) {
        onNodesChange(changes);
        return;
      }
      set((state) => withNodes(state.nodeLookup, applyNodeChanges(changes, state.nodes)));
    },
    setOnEdgesChange: (onEdgesChange) => set({ onEdgesChange }),
    triggerEdgeChanges: (changes) => {
      if (changes.length === 0) return;
      const { onEdgesChange } = get();
      if (onEdgesChange) {
        onEdgesChange(changes);
        return;
      }
      set((state) => withEdges(applyEdgeChanges(changes, state.edges)));
    },
    applySelection: ({ nodeChanges, edgeChanges }) => {
      if (!get().elementsSelectable) return;
      get().triggerNodeChanges(nodeChanges);
      get().triggerEdgeChanges(edgeChanges);
    },
    setConnection: (connection) => set({ connection }),
    setClickConnectStart: (clickConnectStart) => set({ clickConnectStart }),
    setConnectOptions: (connectOptions) => set({ connectOptions }),
    setConnectHandlers: (connectHandlers) => set({ connectHandlers }),
    setDeleteHandlers: (deleteHandlers) => set({ deleteHandlers }),
    setElementHandlers: (elementHandlers) => set({ elementHandlers }),
    setDeleteKeyCode: (deleteKeyCode) => set({ deleteKeyCode }),
    setSelectionRect: (selectionRect) => set({ selectionRect }),
    setDomNode: (domNode) => set({ domNode }),
    deleteSelected: () => {
      const { nodes, edges, deleteHandlers } = get();
      const removed = getElementsToDelete(nodes, edges);
      if (removed.edges.length > 0) {
        deleteHandlers.onEdgesDelete?.(removed.edges);
        get().triggerEdgeChanges(removed.edges.map(({ id }) => ({ id, type: 'remove' })));
      }
      if (removed.nodes.length > 0) {
        deleteHandlers.onNodesDelete?.(removed.nodes);
        get().triggerNodeChanges(removed.nodes.map(({ id }) => ({ id, type: 'remove' })));
      }
    },
  }));
